import { produce } from 'immer';
import {
    AnyAction,
    createSlice,
    PayloadAction,
    Reducer,
} from '@reduxjs/toolkit';

import {
    Documents,
    DocUploadApiEndpoints,
    DocUploadState,
    DocUploadSubmit,
    DocUploadSubmitFromBrowser,
    SessionInfo,
    UpdateDocuments,
    UpdateDocumentsInBrowser,
    UpdateSessionStatus,
    UploadSessionStatus,
} from './docUploadTypes';

import { docUploadDefaultState as initialState } from './docUploadStateDefaultValue';
import { ApiStatus } from '../../../types';
import { DropdownMenuOptions } from '../../../sharedComponents/DropdownWithTips/types';

const docUploadSlice = createSlice({
    name: 'docUpload',
    initialState,
    reducers: {
        updateDocumentsEndpoints: (state, action: PayloadAction<DocUploadApiEndpoints>) => {
            state.apiEndpoints = action.payload;
        },
        updateApplicationNumberToDocUpload: (state, action: PayloadAction<string>) => {
            state.applicationNumber = action.payload;
        },
        getDocuments(state) {
            state.getDocInfoApiStatus = ApiStatus.Pending;
        },
        getDocumentsFulfilled(state, action: PayloadAction<Documents>) {
            const { 
                allowedFileFormats, 
                maxFileSize, 
                minFileAllowed, 
                maxFileAllowed, 
                categories, 
                docUploadContent, 
                categoriesRequired,
                mandatoryCategories,
                maxTotalFilesSize,
                reworkUploadedFiles,
            } = action.payload;

            state.getDocInfoApiStatus = ApiStatus.Fulfilled;
            state.categories = categories;
            state.mandatoryCategories = mandatoryCategories ? mandatoryCategories: [];
            state.allowedFileFormats = allowedFileFormats;
            state.maxFileSize = Number(maxFileSize);
            state.minFileAllowed = Number(minFileAllowed);
            state.maxFileAllowed = Number(maxFileAllowed);
            state.maxTotalFilesSize = Number(maxTotalFilesSize);
            state.docUploadInfo = categoriesRequired;
            state.reworkUploadedFiles = reworkUploadedFiles;

            if (docUploadContent) {
                state.content = JSON.parse(docUploadContent);
            }
        },
        getDocumentsRejected(state) {
            state.getDocInfoApiStatus = ApiStatus.Rejected;
        },
        updateDocuments(state, action: PayloadAction<UpdateDocuments>) {
            state.docUploadApiStatus = ApiStatus.Pending;
        },
        updateDocumentsFulfilled(state) {
            state.docUploadApiStatus = ApiStatus.Fulfilled;
        },
        updateDocumentsRejected(state) {
            state.docUploadApiStatus = ApiStatus.Rejected;
        },
        docUploadSubmit: (state, action: PayloadAction<DocUploadSubmit>) => {
            state.docUploadApiStatus = ApiStatus.Pending;
        },
        docUploadSubmitFulfilled(state) {
            state.docUploadApiStatus = ApiStatus.Fulfilled;
        },
        docUploadSubmitRejected(state) {
            state.docUploadApiStatus = ApiStatus.Rejected;
        },
        updateSessionStatus(state, action: PayloadAction<UpdateSessionStatus>) {
            return produce(state, (draftState) => {
                const {
                    categoryId,
                    id,
                    sessionId,
                    documentTypeId,
                    status,
                    fileName,
                    progress,
                    errorMsg,
                    description,
                } = action.payload;

                const changedDocUploadData = draftState.docUploadInfo.filter(item => item.categoryName === categoryId)[0].data;
                const changedSubCategory = changedDocUploadData.filter(item => item.id === id)[0];
                const changeSessionStatus = changedSubCategory.sessionInfo?.filter(item => item.sessionId === sessionId)[0];

                Object.assign(changeSessionStatus, {
                    sessionId: sessionId,
                    fileName: fileName,
                    documentTypeId: documentTypeId,
                    description: description,
                    status: status,
                    progress: progress,
                    errorMsg: errorMsg,
                });

                if (action.payload.status === UploadSessionStatus.SuccessfullyUploaded) {
                    Object.assign(changedSubCategory.sessionInfo, [
                        ...changedSubCategory.sessionInfo,
                        {
                            sessionId: sessionId + 1,
                            fileName: draftState.content.alreadyUploadedText && fileName.includes(draftState.content.alreadyUploadedText) ? '': fileName,
                            documentType: '',
                            status: UploadSessionStatus.ShowUploadLink,
                            progress: 0,
                            errorMsg: '',
                        },
                    ]);
                }
            });
        },
        updateDocUploadValidation(state, action: PayloadAction<string>) {
            state.getDocUploadValidation = action.payload;
        },
        getDocUploadDataInvalid(state, action: PayloadAction<DocUploadSubmit>) {
            state.getDocInfoApiStatus = ApiStatus.Pending;
        },
        updateDocumentInBrowser(state, action: PayloadAction<UpdateDocuments>){
            state.docUploadBrowserStatus = ApiStatus.Pending;
        },
        updateDocumentInBrowserFullfilled(state, action: PayloadAction<UpdateDocumentsInBrowser>){
            if(state.uploadedDocuments){
                state.uploadedDocuments = [...state.uploadedDocuments, action.payload];
                state.docUploadBrowserStatus = ApiStatus.Fulfilled;
            }
        },
        updateDocumentInBrowserRejected(state){
            state.docUploadBrowserStatus = ApiStatus.Rejected;
        },
        updateAlreadyUploadedOption: (state, action: PayloadAction<DropdownMenuOptions>) => 
            produce(state, (draftState) => {
                draftState.docUploadInfo.forEach(doc => {
                    if(!draftState?.mandatoryCategories?.includes(doc.categoryName)) {
                        doc.data.forEach(data => {
                            if(!data.documentTypes.some(type => type.id.includes(action.payload.id)) && !data.sessionInfo.some(s => 
                                draftState.content.alreadyUploadedText && s.fileName.includes(draftState.content.alreadyUploadedText)) &&
                                 !(data.sessionInfo.length > 1)){
                                data.documentTypes.push({ ...action.payload, id: `${action.payload.id}-${doc.categoryName}-${data.subCategory}` });
                            }
                        });
                    }
                });
            }),
        deleteAlreadyUploadedOption: (state, action:PayloadAction<{categoryName: string, subCategory: string}>) => 
            produce(state, (draftState) => {
                if(!draftState?.mandatoryCategories?.includes(action.payload.categoryName)){
                    const changedDocUploadData = draftState.docUploadInfo.filter(doc => doc.categoryName === action.payload.categoryName)[0];
                    const changedSubCategory = changedDocUploadData.data.filter(data => data.subCategory === action.payload.subCategory)[0];
                    const deleteDocumentTypeIndex = changedSubCategory.documentTypes.findIndex(type => 
                        draftState?.content.alreadyUploadOption && type.id.includes(draftState?.content.alreadyUploadOption?.id));

                    if(deleteDocumentTypeIndex != -1){
                        changedSubCategory.documentTypes.splice(deleteDocumentTypeIndex, 1);
                    }
                }
            }),
        deleteUploadedFile: (state, action: PayloadAction<{categoryName: string,
            cardId: string, sessionInfo: SessionInfo}>) => produce(state, (draftState) => {
            const {
                categoryName,
                cardId,
                sessionInfo,
            } = action.payload;

            const changedDocUploadData = draftState.docUploadInfo.filter(item => item.categoryName === categoryName)[0].data;
            const changedSubCategory = changedDocUploadData.filter(item => item.id === cardId)[0];
            const deleteSessionInfoIndex = changedSubCategory.sessionInfo.findIndex(s => s.sessionId === sessionInfo.sessionId);
            const defaultSessionInfo = [{ sessionId: 0,
                fileName: '',
                applicationNumber: '',
                status: UploadSessionStatus.Initialize,
                subCategory: changedSubCategory.subCategory,
                subCategoryId: changedSubCategory.subCategoryId,
                id: cardId,
                categoryId: categoryName,
                description: '',
                documentTypeId: '',
                progress: 0,
                errorMsg: '' }];

            changedSubCategory.sessionInfo.splice(deleteSessionInfoIndex, 1);
            if(draftState.uploadedDocuments){
                const deleteUploadedDcoumentIndex = draftState.uploadedDocuments.findIndex(doc => doc.documentDetail.id === cardId && 
                    doc.documentDetail.categoryId === categoryName && doc.sessionId === sessionInfo.sessionId);
                const deleteUploadedDcoument = draftState.uploadedDocuments[deleteUploadedDcoumentIndex];
    
                if(deleteUploadedDcoumentIndex !== -1 && draftState.content.alreadyUploadedText && 
                    !sessionInfo.fileName.includes(draftState.content.alreadyUploadedText)) {
                    const deleteDcoumentDetail = deleteUploadedDcoument.documentDetail;
                    const delCategoryIdAndSubCategory = `${deleteDcoumentDetail.categoryId}_${deleteDcoumentDetail.subCategory}`;
                    const delDocTypeIdAndSessionId = `${deleteDcoumentDetail.documentTypeId}_${deleteUploadedDcoument.sessionId}`;

                    draftState.docUploadInfo.flatMap(doc => doc.data.flatMap(data => data.sessionInfo.forEach((session, index) => {
                        if(draftState.content.alreadyUploadedText && session.fileName?.includes(draftState.content.alreadyUploadedText) && 
                        session.documentTypeId.includes(delDocTypeIdAndSessionId) && session.description.includes(delCategoryIdAndSubCategory)){
                            const mappedDocsTypeId = session.documentTypeId.split('-');
                            const mappedDesc = session.description.split('-');

                            mappedDesc.splice(mappedDesc.indexOf(delCategoryIdAndSubCategory), 1);
                            mappedDocsTypeId.splice(mappedDocsTypeId.indexOf(delDocTypeIdAndSessionId), 1);
                            if(mappedDocsTypeId.length > 0 && mappedDesc.length > 0){
                                Object.assign(session, {
                                    documentTypeId: mappedDocsTypeId.join('-'),
                                    description: mappedDesc.join('-'),
                                });
                            } else {
                                data.sessionInfo.splice(index, 1);
                                if(data.sessionInfo.length === 1){
                                    Object.assign(data.sessionInfo, defaultSessionInfo);
                                }
                            }
                        }
                    })));
                    draftState.uploadedDocuments?.splice(deleteUploadedDcoumentIndex, 1);
                }
            }
            if (changedSubCategory.sessionInfo.length === 1) {
                Object.assign(changedSubCategory.sessionInfo, defaultSessionInfo);
            }
        }),
        docUploadSubmitFromBrowser(state, action: PayloadAction<DocUploadSubmitFromBrowser>){
            state.docUploadSubmitFromBrowserStatus = ApiStatus.Pending;
        },
        docUploadSubmitFromBrowserFullfilled(state){
            state.docUploadSubmitFromBrowserStatus = ApiStatus.Fulfilled;
            
        },
        docUploadSubmitFromBrowserRejected(state){
            state.docUploadSubmitFromBrowserStatus = ApiStatus.Rejected;
        },
    },
});

export const {
    updateDocumentsEndpoints,
    updateApplicationNumberToDocUpload,
    getDocuments,
    getDocumentsFulfilled,
    getDocumentsRejected,
    updateDocuments,
    updateDocumentsFulfilled,
    updateDocumentsRejected,
    docUploadSubmit,
    docUploadSubmitFulfilled,
    docUploadSubmitRejected,
    updateSessionStatus,
    updateDocUploadValidation,
    getDocUploadDataInvalid,
    deleteUploadedFile,
    updateDocumentInBrowser,
    updateDocumentInBrowserFullfilled,
    updateDocumentInBrowserRejected,
    updateAlreadyUploadedOption,
    deleteAlreadyUploadedOption,
    docUploadSubmitFromBrowser,
    docUploadSubmitFromBrowserFullfilled,
    docUploadSubmitFromBrowserRejected,
} = docUploadSlice.actions;

export const docUploadReducer: Reducer<DocUploadState, AnyAction> =
    docUploadSlice.reducer;
